import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { LatLng } from 'leaflet';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { VisitLogEntry } from '../../../types/VisitLogEntry';

import { getSightings } from '../../../services/sighting.service';
import { getVisitFacilityJourney } from '../../../services/visit.service';

import { useVisitGeofences } from '../../../hooks/useVisitGeofences';

import VisitJourneyMap from './VisitJourneyMap/VisitJourneyMap';
import { JourneyFacility } from './VisitJourneyMap/VisitJourneyPanel';

interface AllSightingsMapProps {
  deviceId: string;
  visitId?: string | null;
}

export default function AllSightingsMap({ deviceId, visitId }: AllSightingsMapProps) {
  const [data, setData] = useState<VisitLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [journeyFacilities, setJourneyFacilities] = useState<JourneyFacility[]>([]);

  const fetchSightings = useCallback(async () => {
    setLoading(true);
    setJourneyFacilities([]);
    try {
      let results: any[] = [];

      if (visitId) {
        const journeyResponse = await getVisitFacilityJourney(visitId);

        const segments = Array.isArray(journeyResponse?.facilityJourney)
          ? journeyResponse.facilityJourney
          : Array.isArray(journeyResponse?.data?.facilityJourney)
            ? journeyResponse.data.facilityJourney
            : Array.isArray(journeyResponse?.data)
              ? journeyResponse.data
              : Array.isArray(journeyResponse?.results)
                ? journeyResponse.results
                : [];

        const enrichedFacilities = segments
          .filter((segment: any) => segment.coordinates?.latitude != null && segment.coordinates?.longitude != null)
          .map((segment: any, index: number) => ({
            id: `${index}-${segment.closestFacilityId ?? 'na'}-${segment.firstSeenDateTime ?? ''}`,
            rowId: `${index}-${segment.closestFacilityId ?? 'na'}-${segment.firstSeenDateTime ?? ''}`,
            facilityId: segment.closestFacilityId ?? '',
            facilityName: segment.closestFacilityName ?? '',
            firstSeenDateTime: segment.firstSeenDateTime ?? '',
            lastSeenDateTime: segment.lastSeenDateTime ?? '',
            totalSightings: segment.sightingsCount ?? 0,
            averageTemperatureF: ((segment.averageTemperature ?? 0) * 9) / 5 + 32,
            averageTemperatureC: segment.averageTemperature ?? 0,
            stationIataCode: segment.stationIataCode ?? '',
            closestFacilityLogId: segment.closestFacilityLogId ?? '',
            location: segment.coordinates,
          }));

        setJourneyFacilities(enrichedFacilities);

        // Sighting rows for a visit don't reliably carry `location`, but the
        // facility-journey aggregation always does (see coordinates above) —
        // so build the map's data points from the enriched facilities instead
        // of the raw sightings response. Mirrors toMapEntries() in VisitHistory.tsx.
        results = enrichedFacilities.map((f) => ({
          id: f.rowId,
          deviceId: '',
          facilityId: f.facilityId,
          facilityName: f.facilityName,
          closestReaderImei: '',
          closestReaderName: f.facilityName,
          firstSeenDateTime: f.firstSeenDateTime,
          lastSeenDateTime: f.lastSeenDateTime,
          rssi: 0,
          temperatureF: f.averageTemperatureF,
          temperatureC: f.averageTemperatureC,
          humidity: 0,
          batteryPercent: 0,
          location: f.location,
          visitId,
          locationMethodDescr: '',
        }));
      } else {
        setJourneyFacilities([]);
        const res = await getSightings({ SentinelId: deviceId, skip: 0, take: 100 });
        results = res.results || [];
        if (fromDate) {
          results = results.filter((s) => moment(s.lastSeenDateTime).isSameOrAfter(moment(fromDate), 'day'));
        }
        if (toDate) {
          results = results.filter((s) => moment(s.lastSeenDateTime).isSameOrBefore(moment(toDate), 'day'));
        }
      }

      const validResults = results.filter((s) => s.location?.latitude != null && s.location?.longitude != null);
      const mapped: VisitLogEntry[] = validResults.map((s) => ({
        id: s.id,
        deviceId: s.deviceId,
        facilityId: s.facilityId ?? '',
        facilityName: s.facilityName ?? '',
        closestReaderImei: s.closestReaderImei ?? s.imei ?? '',
        closestReaderName: s.closestReaderName ?? s.readerName ?? '',
        firstSeenDateTime: s.firstSeenDateTime ? moment(s.firstSeenDateTime).toISOString() : '',
        lastSeenDateTime: s.lastSeenDateTime ? moment(s.lastSeenDateTime).toISOString() : '',
        rssi: s.rssi ?? 0,
        temperatureF: s.temperatureF ?? 0,
        temperatureC: s.temperatureC ?? 0,
        humidity: s.humidity ?? 0,
        batteryPercent: s.batteryPercent ?? 0,
        location: s.location,
        visitId: s.visitId ?? '',
        locationMethodDescr: s.locationMethodDescr ?? '',
      }));
      setData(mapped);
    } finally {
      setLoading(false);
    }
  }, [deviceId, visitId, fromDate, toDate]);

  useEffect(() => {
    fetchSightings();
  }, [fetchSightings]);

  const coords = useMemo(() => data.map((e) => [e.location.latitude, e.location.longitude] as [number, number]), [data]);

  const center = useMemo(() => (data.length > 0 ? new LatLng(data[0].location.latitude, data[0].location.longitude) : new LatLng(0, 0)), [data]);

  const uniqueReaders = useMemo(() => {
    const seen = new Set<string>();
    return data.reduce(
      (acc, e) => {
        if (e.closestReaderImei && !seen.has(e.closestReaderImei)) {
          seen.add(e.closestReaderImei);
          acc.push({
            lat: e.location.latitude,
            lng: e.location.longitude,
            name: e.closestReaderName,
            imei: e.closestReaderImei,
          });
        }
        return acc;
      },
      [] as { lat: number; lng: number; name: string; imei: string }[],
    );
  }, [data]);

  // Anchor the geofence fetch on the first sighting that has a location.
  // No journey context here, so every area geofence renders in the standard
  // (purple) tier.
  const geofenceAnchorLocation = useMemo(() => {
    const withLoc = data.find((e) => e.location?.latitude != null);
    return withLoc?.location ?? null;
  }, [data]);

  const { geofences } = useVisitGeofences({
    location: geofenceAnchorLocation,
    journeyFacilities,
  });

  const uniqueFacilities = useMemo(() => {
    const seen = new Map<string, { lat: number; lng: number; name: string; bounds: [number, number][] }>();
    journeyFacilities.forEach((facility) => {
      if (!facility.location || seen.has(facility.rowId)) return;
      const { latitude: lat, longitude: lng } = facility.location;
      const pad = 0.0008;
      seen.set(facility.rowId, {
        lat,
        lng,
        name: facility.facilityName,
        bounds: [
          [lat + pad, lng - pad],
          [lat + pad, lng + pad],
          [lat - pad, lng + pad],
          [lat - pad, lng - pad],
        ],
      });
    });
    return Array.from(seen.values());
  }, [journeyFacilities]);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mt: 1 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant='subtitle2' fontWeight={600}>
          {visitId ? `Visit Journey: ${visitId}` : 'Complete Visit Journey'} {/* ← shows which visit */}
        </Typography>

        {loading && <CircularProgress size={14} sx={{ color: '#7c3aed' }} />}
        {!visitId && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={fromDate ? moment(fromDate) : null}
              label='From'
              format='YYYY-MM-DD'
              sx={{ width: '140px', ml: 2 }}
              slotProps={{ textField: { size: 'small' } }}
              onChange={(date) => setFromDate(date ? date.toDate() : null)}
            />
            <DatePicker
              value={toDate ? moment(toDate) : null}
              label='To'
              format='YYYY-MM-DD'
              sx={{ width: '140px', ml: 1 }}
              slotProps={{ textField: { size: 'small' } }}
              onChange={(date) => setToDate(date ? date.toDate() : null)}
            />
          </LocalizationProvider>
        )}
      </Box>
      <Box sx={{ height: 600, position: 'relative' }}>
        {loading ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
          </Box>
        ) : (
          <VisitJourneyMap
            filtered={data}
            exactCoords={coords}
            spreadPositions={coords}
            center={center}
            activePosition={null}
            activeIdx={null}
            hoveredIdx={null}
            uniqueReaders={uniqueReaders}
            uniqueFacilities={uniqueFacilities}
            geofences={geofences}
            onMarkerClick={() => void 0}
            onMarkerHover={() => void 0}
          />
        )}
        {!loading && data.length === 0 && (
          <Typography variant='body2' color='text.secondary' align='center' sx={{ py: 2 }}>
            No sightings found for this device and date range.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
